"use client"
import { useEffect, useState, useMemo } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Plus, X, Briefcase, ChevronDown, Trash2, Save, Building2, Edit3 } from "lucide-react"
import Dashboard from "../../components/dashboard/Dashboard"
import axios from "axios"
import { ToastContainer, toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import config from "../../config"
import ValidationErrorSidebar from '../../components/ui/ValidationErrorSidebar';
import { validationPatterns as centralizedPatterns } from '../../utils/validationPatterns';
import { usePageSave } from '../../contexts/PageSaveContext'
import { validateDeductionsVsGrossForProspectiveUpdate, exceedDeductionErrorMessage } from '../../utils/financialGuards'
import { useServerPageStatus } from '../../contexts/ServerPageStatusContext';

// Override TAN pattern locally per user-provided specification (10 chars with approved prefixes)
const EMPLOYER_TAN_REGEX = /^(HYD|VPN|BBN|BPL|JBP|CHE|CMB|MRI|DEL|CAL|MRT|AHM|BRD|RKT|SRT|BLR|AGR|KNP|CHN|TVD|ALD|LKN|MUM|NGP|AMR|JLD|PTL|RTK|KLP|NSK|PNE|PTN|RCH|JDH|JPR|SHL)[A-Z][0-9]{5}[A-Z]$/;

const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

const createDefaultSalaryData = (id) => ({
  id,
  employerId: null,
  employerName: "",
  employer: [
    { id: "a", label: "Name", value: "" },
    { id: "b", label: "TAN", value: "" },
    { id: "c", label: "Flat / Building", value: "" },
    { id: "d", label: "Street / Road", value: "" },
    { id: "e", label: "Area / Locality", value: "" },
    { id: "f", label: "Town / City", value: "" },
    { id: "g", label: "State", value: "" },
    { id: "h", label: "Pin Code", value: "" },
    { id: "i", label: "Country", value: "" },
    { id: "j", label: "Employer Category", value: "", options: [] },
  ],
  grossSalaryItems: [
    {
      id: "a",
      label: "A. Salary as per section 17(1) / AIS",
      value: "0",
      child: true,
      data: [
        { id: "a1", label: "i. Basic Salary", value: "0" },
        { id: "a2", label: "ii. DA (Dearness Allowance)", value: "0" },
        { id: "a3", label: "iii. Other", value: "0" },
      ],
    },
    { id: "b", label: "B. Value of perquisites as per section 17(2)", value: "0" },
    { id: "c", label: "C. Profit in lieu of salary as per section 17(3)", value: "0" },
    {
      id: "d",
      label: "D. Income from retirement benefit account maintained in a notified country u/s 89A",
      value: "0",
      child: true,
      data: [
        { id: "d1", label: "i. United States", value: "0" },
        { id: "d2", label: "ii. United Kingdom", value: "0" },
        { id: "d3", label: "iii. Canada", value: "0" },
      ],
    },
    {
      id: "e",
      label: "E. Income from retirement benefit account maintained in a country other than notified country u/s 89A",
      value: "0",
    },
  ],
  allowanceItems: [
    { id: "a", label: "Sec 10(5)-Leave Travel concession/assistance", value: "0", sectionCode: "10(5)" },
    { id: "b", label: "Sec 10(6)-Remuneration received as an official, by whatever name called, of an embassy, high commission etc.", value: "0", sectionCode: "10(6)" },
    { id: "c", label: "Sec 10(7)-Allowances or perquisites paid or allowed as such outside India by the Government to a citizen of India for rendering service outside India", value: "0", sectionCode: "10(7)" },
    { id: "d", label: "Sec 10(10)-Death-cum-retirement gratuity received", value: "0", sectionCode: "10(10)" },
    { id: "e", label: "Sec 10(10A)-Commuted value of pension received", value: "0", sectionCode: "10(10A)" },
    { id: "f", label: "Sec 10(10AA)-Earned leave encashment on Retirement", value: "0", sectionCode: "10(10AA)" },
    { id: "g", label: "Sec 10(10B) First proviso - Compensation limit notified by CG in the Official Gazette", value: "0", sectionCode: "10(10B)(i)" },
    { id: "h", label: "Sec 10(10B) Second proviso - Compensation under scheme approved by the Central Government", value: "0", sectionCode: "10(10B)(ii)" },
    { id: "i", label: "Sec 10(10C)-Amount received/receivable on voluntary retirement or termination of service", value: "0", sectionCode: "10(10C)" },
    { id: "j", label: "Sec 10(10CC)-Tax paid by employer on non-monetary perquisite", value: "0", sectionCode: "10(10CC)" },
    {
      id: "k", label: "Sec 10(13A)-Allowance to meet expenditure incurred on house rent", value: "0", sectionCode: "10(13A)", child: true, data: [
        { id: "k1", label: "i. Place Of Work", value: "", options: true },
        { id: "k2", label: "ii. HRA received", value: "" },
        { id: "k3", label: "iii. Rent Paid", value: "" },
        { id: "k4", label: "iv. Exempt Allowance", value: "" },
      ],
    },
    { id: "l", label: "Section 10(14)(i) - Allowances referred in sub-clauses (a) to (c) of sub-rule (1) in Rule 2BB", value: "0", sectionCode: "10(14)(i)" },
    { id: "m", label: "Section 10(14)(ii) - Transport allowance granted to certain physically handicapped assessee", value: "0", sectionCode: "10(14)(ii)" },
    { id: "n", label: "10(14)(i)(115BAC)", value: "0", sectionCode: "10(14)(i)(115BAC)" },
    { id: "o", label: "10(14)(ii)(115BAC)", value: "0", sectionCode: "10(14)(ii)(115BAC)" },
    { id: "p", label: "EIC", value: "0", sectionCode: "EIC" },
    { id: "q", label: "Any Other", value: "0", sectionCode: "OTH", is_modal: true },
  ],
  deductionItems: [
    { id: "a", label: "Standard Deduction u/s 16(ia)", value: "0" },
    { id: "b", label: "Entertainment Allowance u/s 16(ii)", value: "0" },
    { id: "c", label: "Professional Tax u/s 16(iii)", value: "0" },
  ],
  incomeClaimedForRelief: [{ id: "a", label: "Income Claimed for Relief from Taxation u/s 89A", value: "0" }],
  grossExpanded: false,
  allowancesExpanded: false,
  deductionItemsExpanded: false,
  lessIncomeExpanded: false,
  netSalaryExpanded: false,
  totallessIncome: 0,
})

function EmployerModal({ isOpen, onClose, employerData, onSave, employerName, salaryId, onValidationChange }) {
  const [formData, setFormData] = useState(employerData)
  const [employerCategoryList, setCountriesList] = useState([])
  const [validationErrors, setValidationErrors] = useState({})

  useEffect(() => {
    setFormData(employerData)
  }, [employerData])

  // Centralized field validator for TAN and PIN
  const validateField = (fieldId, value) => {
    let patternKey = null
    if (fieldId === 'b') patternKey = 'TAN' // Employer TAN
    if (fieldId === 'h') patternKey = 'PIN' // Employer PIN
    if (!patternKey) return ''

    const pattern = centralizedPatterns[patternKey]
    const stringValue = (value || '').trim()
    let error = ''

    // Validate only when expected full length is reached
    const requiredLength = patternKey === 'TAN' ? 10 : patternKey === 'PIN' ? 6 : null
    if (stringValue && requiredLength && stringValue.length === requiredLength && !pattern.test(stringValue)) {
      error = `Invalid ${patternKey} format.`
    }
    return error
  }

  const handleInputChange = (id, value) => {
    if (id === 'b' && typeof value === 'string') {
      value = value.toUpperCase();
    }
    setFormData((prev) => prev.map((field) => (field.id === id ? { ...field, value } : field)))

    // Validate TAN (id 'b') and PIN (id 'h') non-blocking
    if (id === 'b' || id === 'h') {
      const error = validateField(id, value)
      if (error) {
        setValidationErrors((prev) => ({ ...prev, [id]: error }))
      } else {
        setValidationErrors((prev) => {
          const next = { ...prev }
          delete next[id]
          return next
        })
      }
    }

    // Emit current employer validation to parent so sidebar can display
    if (onValidationChange) {
      const nextForm = formData.map((f) => (f.id === id ? { ...f, value } : f))
      const errs = {}
      const tan = (nextForm.find(f => f.id === 'b')?.value || '').trim()
      const tanUpper = tan.toUpperCase()
      if (tanUpper && !EMPLOYER_TAN_REGEX.test(tanUpper)) {
        errs[`${salaryId}_employer_b`] = 'Invalid TAN format.'
      }
      const pin = (nextForm.find(f => f.id === 'h')?.value || '').trim()
      if (pin && pin.length === 6 && !centralizedPatterns.PIN.test(pin)) {
        errs[`${salaryId}_employer_h`] = 'Invalid PIN format.'
      }
      const category = (nextForm.find(f => f.id === 'j')?.value || '').trim()
      const nameVal = (nextForm.find(f => f.id === 'a')?.value || '').trim()
      if (category !== 'NA' && !nameVal) {
        errs[`${salaryId}_employer_a`] = 'Employer name is required'
      }
      onValidationChange(errs)
    }
  }

  const handleSave = () => {
    const employmentCategory = formData.find(f => f.id === "j")?.value || ""
    const employerName = formData.find(f => f.id === "a")?.value || ""
    const tan = (formData.find(f => f.id === 'b')?.value || '').trim().toUpperCase()
    const pin = (formData.find(f => f.id === 'h')?.value || '').trim()

    // Build errors for sidebar if any
    const errs = {}
    if (employmentCategory !== "NA" && !employerName) {
      errs[`${salaryId}_employer_a`] = 'Employer name is required'
    }
            if (tan && !EMPLOYER_TAN_REGEX.test(tan)) {
      errs[`${salaryId}_employer_b`] = 'Invalid TAN format.'
    }
    if (pin && pin.length === 6 && !centralizedPatterns.PIN.test(pin)) {
      errs[`${salaryId}_employer_h`] = 'Invalid PIN format.'
    }

    if (onValidationChange) onValidationChange(errs)
    if (Object.keys(errs).length > 0) {
      setValidationErrors(prev => ({
        ...prev,
        [`${salaryId}_employer_validation`]: 'Please fix employer details errors'
      }))
      return
    }

    onSave(formData)
    onClose()
  }

  const handleClose = () => {
    setFormData(employerData)
    onClose()
  }

  const isEmployerNameRequired = (employerData) => {
    const employmentCategory = employerData?.find(f => f.id === "j")?.value || ""
    return employmentCategory !== "NA"
  }


  const getAllCodeList = async (codeType, setDataFn) => {
    try {
      const response = await axios.get(`${config.apiUrl}/system/code/${codeType}`)
      const data = response.data

      if (data && data.codes?.length > 0) {
        setDataFn(data.codes)
      } else {
        setDataFn([])
      }
    } catch (error) {
      console.log(`Error fetching ${codeType}:`, error)
    }
  }

  useEffect(() => {
    getAllCodeList("EmployerCategory", setCountriesList)
  }, [])

  if (!isOpen) return null

  return (
    <AnimatePresence>

      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black bg-opacity-50"
          onClick={handleClose}
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative bg-white rounded-xl shadow-2xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden"
        >
          <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-cyan-50 to-blue-50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-500 rounded-lg">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-800">Employer Details</h2>
                <p className="text-sm text-gray-600">{employerName || "Update employer information"}</p>
              </div>
            </div>
            <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {formData.map((field) => (
                <div key={field.id} className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {field.label}
                    {field.id === "a" && (
                      <span className="text-red-500 ml-1">
                        {formData.find(f => f.id === "j")?.value !== "NA" ? "*" : ""}
                      </span>
                    )}
                  </label>
                  {field.options ? (
                    <select
                      onChange={(e) => handleInputChange(field.id, e.target.value)}
                      value={field.value}
                      className="w-full px-3 py-2 border rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    >
                      <option value="">Select Category</option>
                      {employerCategoryList?.map((e) => (
                        <option key={e.codeValue} value={e.codeValue}>
                          {e.description}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={field.value}
                      onChange={(e) => handleInputChange(field.id, e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 hover:border-gray-400"
                      placeholder={`Enter ${field.label.toLowerCase()}`}
                      data-salary-id={salaryId || 1}
                      data-section="employer"
                      data-field={field.id}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              onClick={handleClose}
              className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSave}
              className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-cyan-500 to-cyan-600 text-white rounded-lg hover:from-cyan-600 hover:to-cyan-700 transition-all duration-200"
            >
              <Save className="w-4 h-4" />
              Save Details
            </motion.button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  )
}

function SalarySection({
  title,
  amount,
  isExpanded,
  onToggle,
  items,
  isMenu,
  onInputChange,
  onChildInputChange,
  totalGrossSalary = 0,
  employmentCategory = "",
  totalBasicSalary,
  totalAllowanceAmount,
  salaryEntries,
  //  New props for custom other sources
  customOtherSources,
  setCustomOtherSources,
  showOtherIncomeModal,
  setShowOtherIncomeModal,
  setValidationErrors,
  clearValidationError

}) {
  const [selectedItem, setSelectedItem] = useState(null)
  // const [showOtherIncomeModal, setShowOtherIncomeModal] = useState(false)
  // const [customOtherSources, setCustomOtherSources] = useState([
  //   { id: "23894759832475893u245", description: "", amount: "0" }
  // ])


  const [placeOfWorkList, setPlaceOfWorkList] = useState([])

  const handleInputClick = (item) => {
    if (item.child) {
      setSelectedItem(item)
    }
  }

  const handleClose = () => setSelectedItem(null)

  const getSalaryInformation = async () => {
    const { basic, da, other } = salaryEntries.reduce(
      (acc, entry) => {
        acc.basic += Number.parseFloat(entry.grossSalaryItems[0]?.data?.[0]?.value || "0");
        acc.da += Number.parseFloat(entry.grossSalaryItems[0]?.data?.[1]?.value || "0");
        acc.other += Number.parseFloat(entry.grossSalaryItems[0]?.data?.[2]?.value || "0");
        return acc;
      },
      { basic: 0, da: 0, other: 0 }
    );

    return { basic, da, other };
  };

  const handleChildInputChangeLocal = (childId, value) => {

    if (!selectedItem) return;

    // Update local UI state immediately
    setSelectedItem(prev => {
      if (!prev) return prev;

      const updatedData = prev.data?.map(child =>
        child.id === childId ? { ...child, value } : child
      ) || [];

      // Special handling for HRA (Section 10(13A))
      if (prev.id === "k") { // 'k' is the HRA section ID
        const hraReceived = Number.parseFloat(
          updatedData.find(d => d.id === "k2")?.value || "0"
        );
        const rentPaid = Number.parseFloat(
          updatedData.find(d => d.id === "k3")?.value || "0"
        );

        // ✅ Calculate total basic salary = Basic + DA from ALL entries
        const basicSalary = salaryEntries.reduce((sum, entry) => {
          return sum + Number.parseFloat(entry.grossSalaryItems[0]?.data?.[0]?.value || "0");
        }, 0);

        const daSalary = salaryEntries.reduce((sum, entry) => {
          return sum + Number.parseFloat(entry.grossSalaryItems[0]?.data?.[1]?.value || "0");
        }, 0);
        const otherSalary = salaryEntries.reduce((sum, entry) => {
          return sum + Number.parseFloat(entry.grossSalaryItems[0]?.data?.[2]?.value || "0");
        }, 0);

        const totalBasicSalary = basicSalary + daSalary;
        // Conditions for exemption
        const condition1 = hraReceived;
        const condition2 = rentPaid - (0.1 * totalBasicSalary);

        // Metro or Non-metro check
        const placeOfWork = updatedData.find(d => d.id === "k1")?.value || "";
        const isMetro = placeOfWork == 1 ? true : false;

        const condition3 = (isMetro ? 0.5 : 0.4) * totalBasicSalary;
        const condition4 = (totalBasicSalary + otherSalary) / 3;


        // ✅ Correct exempt calculation
        const exemptAmount = Math.max(0, Math.min(condition1, condition2, condition3, condition4));

        // Update exempt amount field (k4)
        const updatedWithExempt = updatedData.map(d =>
          d.id === "k4" ? { ...d, value: exemptAmount.toFixed(2) } : d
        );

        return { ...prev, data: updatedWithExempt };
      }

      return { ...prev, data: updatedData };
    });

    // Propagate change to parent component
    if (onChildInputChange) {
      onChildInputChange(selectedItem.id, childId, value);
    }
  };


  const handleSaveAndClose = () => {
    handleClose()
  }

  const addCustomOtherSource = () => {
    const newId = customOtherSources.length > 0 ? Math.max(...customOtherSources.map(s => s.id)) + 1 : 1
    setCustomOtherSources([
      ...customOtherSources,
      { id: newId, description: "", amount: "0" }
    ])
  }

  const updateCustomOtherSource = (id, field, value) => {
    console.log("Updating source", id, field, value)
    setCustomOtherSources(prev =>
      prev.map(source =>
        source.id === id ? { ...source, [field]: value } : source
      )
    )
  }

  const removeCustomOtherSource = (id) => {

    setCustomOtherSources(prev => prev.filter(source => source.id !== id))
  }

  // In the SalarySection component, update the save handler:
  const saveOtherIncomeSources = () => {
    const totalCustomAmount = customOtherSources.reduce(
      (sum, source) => sum + Number.parseFloat(source.amount || "0"),
      0
    )

    // Update the "Any Other" item value with the total from custom sources
    if (onInputChange) {
      onInputChange("q", totalCustomAmount.toFixed(2))
    }

    setShowOtherIncomeModal(false)
  }

  // In the main component, add this useEffect:


  const getAllCodeList = async (codeType, setDataFn) => {
    try {
      const response = await axios.get(`${config.apiUrl}/system/code/${codeType}`)
      const data = response.data

      if (data && data.codes?.length > 0) {
        setDataFn(data.codes)
      } else {
        setDataFn([])
      }
    } catch (error) {
      console.log(`Error fetching ${codeType}:`, error)
    }
  }

  /**
 * Validates if the claimed relief u/s 89A exceeds total income from notified countries
 * @param {number} claimedRelief - Value entered for "Income claimed for relief u/s 89A"
 * @returns {boolean} - Returns false if validation fails (claimed relief > total 89A income)
 */
  const validateSection89A = (claimedRelief) => {

    if (!Array.isArray(salaryEntries)) {
      console.error("salaryEntries must be an array");
      return false;
    }

    // Calculate total income from all notified countries (US + UK + Canada) across all salaries
    const total89AIncome = salaryEntries.reduce((total, entry) => {
      const usIncome = Number.parseFloat(entry.grossSalaryItems[3]?.data?.[0]?.value || "0"); // US
      const ukIncome = Number.parseFloat(entry.grossSalaryItems[3]?.data?.[1]?.value || "0"); // UK
      const canadaIncome = Number.parseFloat(entry.grossSalaryItems[3]?.data?.[2]?.value || "0"); // Canada
      return total + usIncome + ukIncome + canadaIncome;
    }, 0);

    // Validation check
    if (Number.parseFloat(claimedRelief || "0") > total89AIncome) {
      setValidationErrors(prev => ({
        ...prev,
        [`section89a_relief_limit`]: `Claimed relief (₹${formatCurrency(claimedRelief)}) cannot exceed total income from notified countries (₹${formatCurrency(total89AIncome)})`
      }));
      return false;
    } else {
      // Clear error if validation passes
      clearValidationError(`section89a_relief_limit`);
    }
    return true;
  };

  const validateField = (item, value) => {
    const numValue = Number.parseFloat(value || "0")

    if (title === "Less: Relief") {
      if (!validateSection89A(value)) return;
      // Clear section 89A error if validation passes
      clearValidationError(`section89a_relief_limit`);
    }

    if (title === "Less: Exempt Allowances") {
      const currentItemValue = Number.parseFloat(item.value || "0")
      const newTotal = totalAllowanceAmount - currentItemValue + numValue

      if (newTotal > totalGrossSalary) {
        // Add to centralized validation errors
        const errorKey = `exempt_allowances_limit_${item.id}`
        setValidationErrors(prev => ({
          ...prev,
          [errorKey]: `Total exempt allowances (₹${newTotal.toFixed(2)}) cannot exceed total gross salary (₹${totalGrossSalary.toFixed(2)})`
        }))
        return false
      } else {
        // Clear error if validation passes
        clearValidationError(`exempt_allowances_limit_${item.id}`)
      }
    }

    if (item.id === "c" && title.includes("Deduction u/s 16")) {
      if (numValue > 5000) {
        setValidationErrors(prev => ({
          ...prev,
          [`professional_tax_limit_${item.id}`]: "Professional tax cannot exceed ₹5000"
        }))
        return false
      } else {
        // Clear error if validation passes
        clearValidationError(`professional_tax_limit_${item.id}`)
      }
    }

    if (item.id === "b" && title.includes("Deduction u/s 16")) {
      if (!employmentCategory) {
        setValidationErrors(prev => ({
          ...prev,
          [`entertainment_allowance_category_${item.id}`]: "Entertainment allowance is only for Govt/PSU employees"
        }))
        return false
      } else {
        // Clear error if validation passes
        clearValidationError(`entertainment_allowance_category_${item.id}`)
      }
      if (numValue > 5000) {
        setValidationErrors(prev => ({
          ...prev,
          [`entertainment_allowance_limit_${item.id}`]: "Entertainment allowance cannot exceed ₹5000"
        }))
        return false
      } else {
        // Clear error if validation passes
        clearValidationError(`entertainment_allowance_limit_${item.id}`)
      }
      if (numValue > 0.2 * totalBasicSalary) {
        setValidationErrors(prev => ({
          ...prev,
          [`entertainment_allowance_limit_percentage_${item.id}`]: "Entertainment allowance cannot exceed 20% of basic salary"
        }))
        return false
      } else {
        // Clear error if validation passes
        clearValidationError(`entertainment_allowance_limit_percentage_${item.id}`)
      }
    }

    if (item.id === "k" && title.includes("Exempt Allowances")) {
      const hraItem = items.find(i => i.id === "k")
      if (hraItem && hraItem.data) {
        const hraReceived = Number.parseFloat(hraItem.data[1]?.value || "0")
        const rentPaid = Number.parseFloat(hraItem.data[2]?.value || "0")
        const exemptAmount = Number.parseFloat(hraItem.data[3]?.value || "0")

        const limit1 = rentPaid - (0.1 * totalBasicSalary)
        const limit2 = (employmentCategory === "Metro" ? 0.5 : 0.4) * totalBasicSalary

        const actualLimit = Math.min(hraReceived, limit1, limit2)

        if (exemptAmount > actualLimit) {
          setValidationErrors(prev => ({
            ...prev,
            [`hra_exemption_limit_${item.id}`]: `HRA exemption cannot exceed ₹${actualLimit.toFixed(2)}`
          }))
          return false
        } else {
          // Clear error if validation passes
          clearValidationError(`hra_exemption_limit_${item.id}`)
        }
      }
    }

    return true
  }



  useEffect(() => {
    getAllCodeList("PlaceOfWork", setPlaceOfWorkList)
  }, [])

  return (
    <>

      <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden mb-4" style={{ borderRadius: "10px" }}>
        <button onClick={onToggle} className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50">
          <span className="font-medium text-gray-700">{title}</span>
          <div className="flex items-center gap-4">
            <span className="font-medium">₹ {amount}</span>
            <motion.div animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.2 }}>
              {isMenu ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <>&nbsp;&nbsp;&nbsp;</>}
            </motion.div>
          </div>
        </button>

        <AnimatePresence initial={false}>
          {isExpanded && isMenu && (
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: "auto" }}
              exit={{ height: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="py-3 bg-gray-50">
                {items.map((item, i) => {
                  // Determine if this field is allowed in new regime
                  const isNewRegimeAllowed = (title.includes("Exempt Allowances") && 
                    ["b", "c", "d", "e", "f", "g", "h", "i", "j", "n", "o", "q"].includes(item.id)) ||
                    (title.includes("Relief") && item.id === "a");
                  
                  const isOldRegimeOnly = title.includes("Deduction u/s 16") && 
                    ["a", "b", "c"].includes(item.id);

                  return (
                    <div key={item.id}>
                      <div className={`flex px-4 py-2 items-center gap-4 ${i % 2 === 0 ? "bg-[#E5F6FE]" : ""}`}>
                        <div className="flex-1">
                          <label className="text-sm text-gray-600">{item.label}</label>
                          {/* Inline informative label with field description */}
                          {isNewRegimeAllowed && (
                            <div className="flex items-center gap-1 mt-1">
                              <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                              <span className="text-xs font-medium text-green-800">Allowed in New Regime</span>
                            </div>
                          )}
                          
                          {isOldRegimeOnly && (
                            <div className="flex items-center gap-1 mt-1">
                              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                              <span className="text-xs font-medium text-blue-800">
                                {item.id === "a" ? "Old regime only - New regime auto calculates" : "Old regime only"}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="relative w-48">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                          <input
                            type={item.type || "text"}
                            className={`w-full pl-8 pr-3 py-2 text-right rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#00A3FF] focus:border-transparent ${item.child ? "cursor-pointer bg-gray-50" : ""}`}
                            value={item.value}
                            onChange={(e) => {
                              if (validateField(item, e.target.value)) {
                                onInputChange && onInputChange(item.id, e.target.value)
                              }
                            }}
                            onClick={() => { item.is_modal ? setShowOtherIncomeModal(true) : handleInputClick(item) }}
                            readOnly={item.child}
                            style={{ borderRadius: "10px" }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {selectedItem && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
            onClick={handleClose}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-xl shadow-lg p-6 w-full max-w-3xl mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-700">
                  Additional Details for <span className="text-cyan-600">{selectedItem.label}</span>
                </h2>
                <button onClick={handleClose}>
                  <X className="w-6 h-6 text-gray-500" />
                </button>
              </div>
              <div className="space-y-5">
                {selectedItem.data?.map((childItem) => (
                  <div key={childItem.id} className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-center">
                    <label className="text-sm text-gray-600 font-medium">{childItem.label}</label>
                    {childItem.options ? (
                      <select
                        className="sm:col-span-2 relative w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00A3FF] focus:border-transparent"
                        value={childItem.value || ""}
                        onChange={(e) => handleChildInputChangeLocal(childItem.id, e.target.value)}
                      >
                        <option value="">Select</option>
                        {placeOfWorkList.map((opt) => (
                          <option key={opt.codeValue} value={opt.codeValue}>
                            {opt.description}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="sm:col-span-2 relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                        <input
                          type="text"
                          className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#00A3FF] focus:outline-none"
                          placeholder={`Enter ${childItem.label.toLowerCase()}`}
                          value={childItem.value || ""}
                          onChange={(e) => handleChildInputChangeLocal(childItem.id, e.target.value)}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleSaveAndClose}
                  className="bg-cyan-600 text-white px-6 py-2 rounded-md hover:bg-cyan-700 transition"
                >
                  Save & Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showOtherIncomeModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
            onClick={() => setShowOtherIncomeModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-xl shadow-lg p-6 w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-800">Other Income Sources</h2>
                <button onClick={() => setShowOtherIncomeModal(false)}>
                  <X className="w-6 h-6 text-gray-500" />
                </button>
              </div>
              <div className="space-y-4">
                {customOtherSources.map((source, index) => (
                  <div key={source.id} className="border rounded-lg p-4 space-y-4">
                    <div className="flex justify-between items-center">
                      <h3 className="font-medium text-gray-700">Source {index + 1}</h3>
                      {customOtherSources.length > 1 && (
                        <button
                          onClick={() => removeCustomOtherSource(source.id)}
                          className="p-2 text-red-500 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Description</label>
                        <input
                          type="text"
                          className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                          placeholder="Enter description"
                          value={source.description}
                          onChange={(e) => updateCustomOtherSource(source.id, "description", e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Amount</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                          <input
                            type="text"
                            className="w-full pl-8 pr-3 py-3 text-right border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                            placeholder="0.00"
                            value={source.amount}
                            onChange={(e) => {
                              // Only allow numbers (0-9) and decimal point
                              const value = e.target.value.replace(/[^0-9.]/g, '');
                              // Prevent multiple decimal points
                              const parts = value.split('.');
                              const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                              const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0)); // Ensure non-negative
                              updateCustomOtherSource(source.id, "amount", numericValue.toString());
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-between items-center pt-4">
                <button
                  onClick={addCustomOtherSource}
                  className="flex items-center gap-2 px-4 py-2 text-cyan-600 border border-cyan-600 rounded-lg hover:bg-cyan-50"
                >
                  <Plus className="w-4 h-4" />
                  Add Another Source
                </button>
                <div className="flex gap-3">
                  <button
                    className="px-4 py-2 bg-gray-300 rounded-md hover:bg-gray-400"
                    onClick={() => setShowOtherIncomeModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-4 py-2 bg-cyan-600 text-white rounded-md hover:bg-cyan-700"
                    onClick={saveOtherIncomeSources}
                  >
                    Save
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

function EmployerSection({ employerName, onClick, employerCategory }) {
  const displayName = employerCategory === "NA"
    ? "Not Applicable"
    : employerName || "Click to add employer information"

  return (
    <motion.div
      whileHover={{ scale: 1.01 }}
      className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer"
      onClick={onClick}
    >
      <div className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-xl">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Employer Details</h3>
              <p className="text-sm text-gray-600 mt-1">{displayName}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-cyan-600">
            <Edit3 className="w-5 h-5" />
            <span className="text-sm font-medium">Edit</span>
          </div>
        </div>

        {employerName && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-sm text-gray-600">Employer information added</span>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  )
}

export default function Salary() {
  const { markPageAsSaved, markPageAsUnsaved } = usePageSave()
  const { refreshAllPageStatuses } = useServerPageStatus()
  const [salaryEntries, setSalaryEntries] = useState([createDefaultSalaryData(1)])
  const [activeTab, setActiveTab] = useState(1)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isEmployerModalOpen, setIsEmployerModalOpen] = useState(false)
  const [employerCategoryList, setCountriesList] = useState([])
  const [showOtherIncomeModal, setShowOtherIncomeModal] = useState(false)
  const [customOtherSources, setCustomOtherSources] = useState([
    { id: "23894759832475893u245", description: "", amount: "0" }
  ])
  const [commonDeductionTab, setCommonDeductionTab] = useState("allowances")
  const [validationErrors, setValidationErrors] = useState({})

  // Function to clear validation errors when fields become valid
  const clearValidationError = (errorKey) => {
    setValidationErrors(prev => {
      const next = { ...prev }
      delete next[errorKey]
      return next
    })
  }

  const addCustomOtherSource = () => {
    const newId = customOtherSources.length > 0 ? Math.max(...customOtherSources.map(s => s.id)) + 1 : 1
    setCustomOtherSources([
      ...customOtherSources,
      { id: newId, description: "", amount: "0" }
    ])
  }

  const updateCustomOtherSource = (id, field, value) => {
    console.log("Updating source....", id, field, value)
    
    // Clear validation errors when custom income source becomes valid
    if (field === "description" && value && value.trim()) {
      clearValidationError(`custom_income_description_${id}`)
    }
    if (field === "amount" && value && Number.parseFloat(value) > 0) {
      clearValidationError(`custom_income_amount_${id}`)
    }
    
    setCustomOtherSources(prev =>
      prev.map(source =>
        source.id === id ? { ...source, [field]: value } : source
      )
    )
  }

  const removeCustomOtherSource = (id) => {
    console.log("Removing source", id)
    // Clear validation errors for the removed source
    clearValidationError(`custom_income_description_${id}`)
    clearValidationError(`custom_income_amount_${id}`)
    setCustomOtherSources(prev => prev.filter(source => source.id !== id))
  }

  const handleRemoveCustomOtherSource = async (id, apiId) => {
    if (apiId) {
      await axios.delete(`${config.apiUrl}/income/salary/allowance/${apiId}`)
        .then(() => {
          removeCustomOtherSource(id)
          toast.success("Other income source removed successfully")
          // Clear remove error if successful
          clearValidationError(`other_income_remove_error`)
        })
        .catch((error) => {
          console.error("Error removing other income source:", error)
          setValidationErrors(prev => ({
            ...prev,
            [`other_income_remove_error`]: "Failed to remove other income source"
          }))
        })
    } else {
      removeCustomOtherSource(id)
    }

  }

  const getAllCodeList = async (codeType, setDataFn) => {
    try {
      const response = await axios.get(`${config.apiUrl}/system/code/${codeType}`)
      const data = response.data

      if (data && data.codes?.length > 0) {
        setDataFn(data.codes)
        // Clear fetch error if successful
        clearValidationError(`fetch_code_error_${codeType}`)
      } else {
        setDataFn([])
        // Clear fetch error if successful (even with empty data)
        clearValidationError(`fetch_code_error_${codeType}`)
      }
    } catch (error) {
      console.log(`Error fetching ${codeType}:`, error)
      setValidationErrors(prev => ({
        ...prev,
        [`fetch_code_error_${codeType}`]: `Failed to fetch ${codeType}.`
      }))
    }
  }

  useEffect(() => {
    getAllCodeList("EmployerCategory", setCountriesList)
  }, [])



  // Calculate exempt allowances for new regime (only specific sections)
  const calculateNewRegimeExemptAllowances = (entry) => {
    const newRegimeExemptSections = [
      "10(6)", "10(7)", "10(10)", "10(10A)", "10(10AA)", 
      "10(10B)(i)", "10(10B)(ii)", "10(10C)", "10(10CC)",
      "10(14)(i)(115BAC)", "10(14)(ii)(115BAC)", "OTH"
    ];
    
    return entry.allowanceItems
      .filter(item => newRegimeExemptSections.includes(item.sectionCode))
      .reduce((sum, item) => sum + Number.parseFloat(item.value || "0"), 0);
  };

  const salaryCalculations = useMemo(() => {
    return salaryEntries.map((entry) => {
      const totalAllowanceItems = entry.allowanceItems.reduce(
        (sum, item) => sum + Number.parseFloat(item.value || "0"),
        0
      )

      // Calculate exempt allowances for new regime
      const newRegimeExemptAllowances = calculateNewRegimeExemptAllowances(entry);

      const section17OneTotal = entry.grossSalaryItems[0]?.data?.reduce(
        (sum, item) => sum + Number.parseFloat(item.value || "0"),
        0
      ) || Number.parseFloat(entry.grossSalaryItems[0]?.value || "0")

      const notifiedCountryTotal = entry.grossSalaryItems[3]?.data?.reduce(
        (sum, item) => sum + Number.parseFloat(item.value || "0"),
        0
      ) || Number.parseFloat(entry.grossSalaryItems[3]?.value || "0")

      const totalGrossSalary =
        section17OneTotal +
        Number.parseFloat(entry.grossSalaryItems[1]?.value || "0") +
        Number.parseFloat(entry.grossSalaryItems[2]?.value || "0") +
        notifiedCountryTotal +
        Number.parseFloat(entry.grossSalaryItems[4]?.value || "0")

      const totalDeduction = entry.deductionItems.reduce(
        (sum, item) => sum + Number.parseFloat(item.value || "0"),
        0
      )
      
      // Calculate total relief (income claimed for relief u/s 89A)
      const totalRelief = entry.incomeClaimedForRelief.reduce(
        (sum, item) => sum + Number.parseFloat(item.value || "0"),
        0
      );
      
      // Old regime calculation
      const calculatedNetSalary = totalGrossSalary - (totalRelief + totalAllowanceItems + totalDeduction);
      const totalNetSalary = Math.max(0, calculatedNetSalary);

      // New regime calculation: Gross Salary - Exempt Allowances - Relief - Standard Deduction
      // Standard deduction is minimum of (Gross - Exempt Allowances - Relief) or 75000
      const standardDeduction = Number.parseFloat(entry.deductionItems.find(item => item.id === "a")?.value || "0");
      const newRegimeBaseAmount = totalGrossSalary - newRegimeExemptAllowances - totalRelief;
      const newRegimeStandardDeduction = Math.min(newRegimeBaseAmount, 75000);
      const newRegimeTaxableSalary = Math.max(0, newRegimeBaseAmount - newRegimeStandardDeduction);

      return {
        id: entry.id,
        totalAllowanceItems,
        newRegimeExemptAllowances,
        totalGrossSalary,
        totalDeduction,
        totalNetSalary,
        newRegimeTaxableSalary,
        newRegimeStandardDeduction
      }
    })
  }, [salaryEntries])

  const getCalculationsForEntry = (entryId) => {
    return (
      salaryCalculations.find((calc) => calc.id === entryId) || {
        totalAllowanceItems: 0,
        newRegimeExemptAllowances: 0,
        totalGrossSalary: 0,
        totalDeduction: 0,
        totalNetSalary: 0,
        newRegimeTaxableSalary: 0,
        newRegimeStandardDeduction: 0
      }
    )
  }

  const validateEmployerCategory = (salaryEntries) => {
    const hasInvalidEmployer = salaryEntries.some(entry => {
      const employerCategory = entry.employer.find(f => f.id === "j")?.value
      const employerName = entry.employer.find(f => f.id === "a")?.value || ""
      
      // Check if THIS specific entry has gross salary
      const entryHasGrossSalary = getCalculationsForEntry(entry.id).totalGrossSalary > 0

      // Check if THIS entry has gross salary but employer category is "Not Applicable"
      const hasInvalidCategory = entryHasGrossSalary && employerCategory === "NA"

      // Check if category requires name but name is missing
      const hasMissingName = employerCategory !== "NA" && !employerName

      return hasInvalidCategory || hasMissingName
    })

    return !hasInvalidEmployer
  }

  const validateExemptAllowances = (salaryEntries) => {
    const totalGrossSalary = salaryEntries.reduce(
      (sum, entry) => sum + getCalculationsForEntry(entry.id).totalGrossSalary,
      0
    )

    const firstEntry = salaryEntries[0]
    if (!firstEntry) return true

    const totalExemptAllowances = firstEntry.allowanceItems.reduce(
      (sum, item) => sum + Number.parseFloat(item.value || "0"),
      0
    )

    return totalExemptAllowances <= totalGrossSalary
  }

  const validateEntertainmentAllowance = (salaryEntries) => {
    const firstEntry = salaryEntries[0]
    if (!firstEntry) return true

    const entertainmentAllowance = Number.parseFloat(
      firstEntry.deductionItems.find(item => item.id === "b")?.value || "0"
    )

    if (entertainmentAllowance > 5000) return false

    const totalBasicSalary = salaryEntries.reduce((sum, entry) => {
      const basicSalary = Number.parseFloat(
        entry.grossSalaryItems[0]?.data?.[0]?.value || "0"
      )
      return sum + basicSalary
    }, 0)

    if (entertainmentAllowance > 0.2 * totalBasicSalary) return false

    const hasValidEmployer = salaryEntries.some(entry => {
      const employmentCategory = entry.employer.find(f => f.id === "j")?.value
      const allowedCategories = ["CGOV", "SGOV", "PSU"]
      return allowedCategories.includes(employmentCategory)
    })

    if (entertainmentAllowance > 0 && !hasValidEmployer) {
      return false
    }

    return true
  }

  const overallTotals = useMemo(() => {
    return {
      totalGrossSalary: salaryCalculations.reduce((sum, calc) => sum + calc.totalGrossSalary, 0),
      totalNetSalary: salaryCalculations.reduce((sum, calc) => sum + calc.totalNetSalary, 0),
    }
  }, [salaryCalculations])

  useEffect(() => {
    localStorage.setItem('total_salary', JSON.stringify(overallTotals))
  }, [overallTotals])

  // Auto-calculate Standard Deduction when overall totals change
  useEffect(() => {
    console.log('=== USEEFFECT AUTO-CALCULATION ===');
    console.log('Overall totals changed:', overallTotals);
    
    if (salaryEntries.length > 0) {
      const autoCalculatedValue = calculateAutoStandardDeduction();
      
      console.log('useEffect: Updating Standard Deduction to:', autoCalculatedValue);
      
      // Update the standard deduction for the first entry (main salary entry)
      setSalaryEntries(prev => prev.map(entry => {
        if (entry.id === 1) {
          return {
            ...entry,
            deductionItems: entry.deductionItems.map(item => {
              if (item.id === "a") {
                return {
                  ...item,
                  value: autoCalculatedValue.toFixed(2)
                };
              }
              return item;
            })
          };
        }
        return entry;
      }));
    }
  }, [overallTotals.totalGrossSalary]) // Run when gross salary changes

  const addSalaryEntry = () => {
    const newId = Math.max(...salaryEntries.map((s) => s.id)) + 1
    const newSalaryEntry = createDefaultSalaryData(newId)
    setSalaryEntries([...salaryEntries, newSalaryEntry])
    setActiveTab(newId)
  }

  useEffect(() => {
    // Update the "Any Other" item value when custom sources change
    const totalCustomAmount = customOtherSources.reduce(
      (sum, source) => sum + Number.parseFloat(source.amount || "0"),
      0
    )

    setSalaryEntries(prev =>
      prev.map(entry => {
        if (entry.id === 1) {
          const updatedAllowanceItems = entry.allowanceItems.map(item =>
            item.id === "q" ? { ...item, value: totalCustomAmount.toFixed(2) } : item
          )
          return { ...entry, allowanceItems: updatedAllowanceItems }
        }
        return entry
      })
    )
  }, [customOtherSources])

  const removeSalaryEntry = async (salaryId) => {
    const entryToRemove = salaryEntries.find((entry) => entry.id === salaryId)

    if (entryToRemove?.employerId) {
      try {
        await axios.delete(
          `${config.apiUrl}/income/salary/employer/${entryToRemove.employerId}`,
          { data: { employerId: entryToRemove.employerId } }
        )
        toast.success("Salary entry deleted successfully!")
        // Clear delete error if successful
        clearValidationError(`delete_salary_error`)
      } catch (error) {
        setValidationErrors(prev => ({
          ...prev,
          [`delete_salary_error`]: "Failed to delete salary entry from server!"
        }))
        console.error("Delete error:", error)
        return
      }
    }

    const updatedEntries = salaryEntries.filter((entry) => entry.id !== salaryId)
    setSalaryEntries(updatedEntries)
    if (activeTab === salaryId && updatedEntries.length > 0) {
      setActiveTab(updatedEntries[0].id)
    }
  }

  const updateSalaryEntry = (salaryId, field, value) => {
    setSalaryEntries((prev) =>
      prev.map((entry) => (entry.id === salaryId ? { ...entry, [field]: value } : entry))
    )
  }

  const validateLTA = (ltaValue, salaryId, sectionId) => {
    const entry = salaryEntries.find(e => e.id === salaryId);
    if (!entry) return true;

    // Calculate TOTAL of ALL salaries' section 17(1) (Basic + DA + Other)
    const section17OneTotal = salaryEntries.reduce((total, entry) => {
      const basic = Number.parseFloat(entry.grossSalaryItems[0]?.data?.[0]?.value || "0");
      const da = Number.parseFloat(entry.grossSalaryItems[0]?.data?.[1]?.value || "0");
      const other = Number.parseFloat(entry.grossSalaryItems[0]?.data?.[2]?.value || "0");
      return total + basic + da + other;
    }, 0);

    if (sectionId == 'a') {
      if (Number.parseFloat(ltaValue || "0") > section17OneTotal) {
        setValidationErrors(prev => ({
          ...prev,
          [`${salaryId}_lta_${sectionId}`]: `LTA cannot exceed Salary under section 17(1) (₹${section17OneTotal.toFixed(2)})`
        }));
        return false;
      } else {
        // Clear error if validation passes
        clearValidationError(`${salaryId}_lta_${sectionId}`);
      }
    }
    if (sectionId == 'e') {
      if (Number.parseFloat(ltaValue || "0") > section17OneTotal) {
        setValidationErrors(prev => ({
          ...prev,
          [`${salaryId}_lta_${sectionId}`]: `Sec 10(10A) cannot exceed Salary under section 17(1) (₹${section17OneTotal.toFixed(2)})`
        }));
        return false;
      } else {
        // Clear error if validation passes
        clearValidationError(`${salaryId}_lta_${sectionId}`);
      }
    }
    if (sectionId == 'f') {
      if (Number.parseFloat(ltaValue || "0") > section17OneTotal) {
        setValidationErrors(prev => ({
          ...prev,
          [`${salaryId}_lta_${sectionId}`]: `Maximum deduction for a non- Government employees including PSU employee is only Rs 25 lakh cannot exceed Salary under section 17(1) (₹${section17OneTotal.toFixed(2)})`
        }));
        return false;
      } else {
        // Clear error if validation passes
        clearValidationError(`${salaryId}_lta_${sectionId}`);
      }
    }
    if (sectionId == 'k') {
      if (Number.parseFloat(ltaValue || "0") > section17OneTotal) {
        setValidationErrors(prev => ({
          ...prev,
          [`${salaryId}_lta_${sectionId}`]: `Sec 10(13A) cannot exceed Salary under section 17(1) (₹${section17OneTotal.toFixed(2)})`
        }));
        return false;
      } else {
        // Clear error if validation passes
        clearValidationError(`${salaryId}_lta_${sectionId}`);
      }
    }
    if (sectionId == 'l') {
      if (Number.parseFloat(ltaValue || "0") > section17OneTotal) {
        setValidationErrors(prev => ({
          ...prev,
          [`${salaryId}_lta_${sectionId}`]: `Sec 10(14)(i) cannot exceed Salary under section 17(1) (₹${section17OneTotal.toFixed(2)})`
        }));
        return false;
      } else {
        // Clear error if validation passes
        clearValidationError(`${salaryId}_lta_${sectionId}`);
      }
    }
    if (sectionId == 'm') {
      if (Number.parseFloat(ltaValue || "0") > section17OneTotal) {
        setValidationErrors(prev => ({
          ...prev,
          [`${salaryId}_lta_${sectionId}`]: `Sec 10(14)(ii) cannot exceed Salary under section 17(1) (₹${section17OneTotal.toFixed(2)})`
        }));
        return false;
      } else {
        // Clear error if validation passes
        clearValidationError(`${salaryId}_lta_${sectionId}`);
      }
    }
    return true;
  };

  // Calculate standard deduction limit: Total Gross Salary - Exempt Allowance - Relief
  const calculateStandardDeductionLimit = () => {
    // Calculate total gross salary across all employers
    const totalGrossSalary = salaryEntries.reduce((total, entry) => {
      const entryCalculations = getCalculationsForEntry(entry.id);
      return total + entryCalculations.totalGrossSalary;
    }, 0);

    // Calculate total exempt allowances across all employers
    const totalExemptAllowance = salaryEntries.reduce((total, entry) => {
      const entryCalculations = getCalculationsForEntry(entry.id);
      return total + entryCalculations.totalAllowanceItems;
    }, 0);

    // Calculate total relief (income claimed for relief u/s 89A)
    const totalRelief = salaryEntries.reduce((total, entry) => {
      return total + entry.incomeClaimedForRelief.reduce((sum, item) => 
        sum + Number.parseFloat(item.value || "0"), 0);
    }, 0);

    // Standard deduction limit = Total Gross Salary - Exempt Allowance - Relief
    const limit = Math.max(0, totalGrossSalary - totalExemptAllowance - totalRelief);
    return limit;
  };

  // Auto-calculate Standard Deduction: Minimum of (₹50,000 OR Total Gross Salary - Exempt Allowances - Relief)
  const calculateAutoStandardDeduction = () => {
    // Calculate total gross salary across all employers
    const totalGrossSalary = salaryEntries.reduce((total, entry) => {
      const entryCalculations = getCalculationsForEntry(entry.id);
      return total + entryCalculations.totalGrossSalary;
    }, 0);

    // Calculate total exempt allowances across all employers
    const totalExemptAllowance = salaryEntries.reduce((total, entry) => {
      const entryCalculations = getCalculationsForEntry(entry.id);
      return total + entryCalculations.totalAllowanceItems;
    }, 0);

    // Calculate total relief (income claimed for relief u/s 89A)
    const totalRelief = salaryEntries.reduce((total, entry) => {
      return total + entry.incomeClaimedForRelief.reduce((sum, item) => 
        sum + Number.parseFloat(item.value || "0"), 0);
    }, 0);

    // Calculate: Total Gross Salary - Exempt Allowances - Relief
    const baseAmount = Math.max(0, totalGrossSalary - totalExemptAllowance - totalRelief);
    
    // Return minimum of ₹50,000 or base amount
    const result = Math.min(50000, baseAmount);
    
    console.log('=== STANDARD DEDUCTION AUTO-CALCULATION ===');
    console.log('Total Gross Salary:', totalGrossSalary);
    console.log('Total Exempt Allowance:', totalExemptAllowance);
    console.log('Total Relief:', totalRelief);
    console.log('Base Amount (Gross - Allowances - Relief):', baseAmount);
    console.log('Auto-calculated Standard Deduction:', result);
    
    return result;
  };

  const handleInputChange = (salaryId, sectionName, id, newValue) => {
    // Mark page as unsaved when data changes
    markPageAsUnsaved('salary')
    
    // Check if this is an amount field that needs validation
    const amountSections = ['grossSalaryItems', 'allowanceItems', 'deductionItems', 'incomeClaimedForRelief'];
    
    let processedValue = newValue;
    if (amountSections.includes(sectionName)) {
      // Only allow numbers (0-9) and decimal point
      processedValue = newValue.replace(/[^0-9.]/g, '');
      // Prevent multiple decimal points
      const parts = processedValue.split('.');
      processedValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : processedValue;
      // Ensure non-negative
      const numericValue = Math.max(0, Number.parseFloat(processedValue || 0));
      processedValue = numericValue.toString();
    }

    // Clear validation errors when field becomes valid
    const clearValidationError = (errorKey) => {
      setValidationErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[errorKey]
        return newErrors
      })
    }

    // Clear LTA validation errors when field becomes valid
    const clearLTAErrors = () => {
      setValidationErrors(prev => {
        const newErrors = { ...prev }
        Object.keys(newErrors).forEach(key => {
          if (key.startsWith(`${salaryId}_lta_`)) {
            delete newErrors[key]
          }
        })
        return newErrors
      })
    }

    // Auto-calculate Standard Deduction when relevant fields change
    const shouldAutoCalculateStandardDeduction = (
      sectionName === "grossSalaryItems" || 
      sectionName === "allowanceItems" || 
      sectionName === "incomeClaimedForRelief"
    );

    if (shouldAutoCalculateStandardDeduction) {
      console.log('=== TRIGGERING AUTO-CALCULATION ===');
      console.log('Section:', sectionName, 'ID:', id, 'Value:', newValue);
      
      // Calculate the auto-calculated standard deduction
      const autoCalculatedStandardDeduction = calculateAutoStandardDeduction();
      
      console.log('Updating Standard Deduction to:', autoCalculatedStandardDeduction);
      
      // Update the standard deduction field with auto-calculated value
      setSalaryEntries(prev => prev.map(entry => {
        if (entry.id === salaryId) {
          return {
            ...entry,
            deductionItems: entry.deductionItems.map(item => {
              if (item.id === "a") {
                return {
                  ...item,
                  value: autoCalculatedStandardDeduction.toFixed(2)
                };
              }
              return item;
            })
          };
        }
        return entry;
      }));
    }

    // Validation for standard deduction limit
    if (sectionName === "deductionItems" && id === "a") {
      const standardDeductionLimit = calculateStandardDeductionLimit();
      const proposedValue = Number.parseFloat(processedValue || "0");
      
      if (proposedValue > standardDeductionLimit) {
        setValidationErrors(prev => ({
          ...prev,
          [`${salaryId}_standard_deduction`]: `Standard deduction cannot exceed ₹${standardDeductionLimit.toLocaleString()}. This is calculated as (Total Gross Salary - Exempt Allowance - Relief).`
        }));
        return;
      } else {
        clearValidationError(`${salaryId}_standard_deduction`)
      }
    }

    // Clear LTA errors when allowance items become valid
    if (sectionName === "allowanceItems" && ["a", "e", "f", "k", "l", "m"].includes(id)) {
      clearLTAErrors()
    }

    // Clear employer validation errors when employer fields become valid
    if (sectionName === "employer") {
      if (id === "a") {
        // Clear employer name error if name is provided
        if (newValue && newValue.trim()) {
          clearValidationError(`${salaryId}_employer_a`)
        }
      }
      if (id === "b") {
        // Clear TAN error if TAN is valid
        const tan = newValue.trim().toUpperCase()
        if (tan && EMPLOYER_TAN_REGEX.test(tan)) {
          clearValidationError(`${salaryId}_employer_b`)
        }
      }
      if (id === "h") {
        // Clear PIN error if PIN is valid
        const pin = newValue.trim()
        if (pin && pin.length === 6 && centralizedPatterns.PIN.test(pin)) {
          clearValidationError(`${salaryId}_employer_h`)
        }
      }
    }

    // Clear child form validation errors when parent value becomes 0
    if (sectionName === "allowanceItems" || sectionName === "grossSalaryItems" || sectionName === "deductionItems") {
      const item = salaryEntries.find(e => e.id === salaryId)?.[sectionName]?.find(i => i.id === id)
      if (item && item.child && item.data && Number.parseFloat(processedValue || "0") === 0) {
        // Clear child validation errors when parent becomes 0
        item.data.forEach(child => {
          clearValidationError(`${salaryId}_${sectionName}_${child.id}`)
        })
      }
    }

    // Validation checks (keep your existing validations)
    if (sectionName === "allowanceItems" && id === "a") {
      if (!validateLTA(processedValue, salaryId, "a")) return;
    }
    if (sectionName === "allowanceItems" && id === "e") {
      if (!validateLTA(processedValue, salaryId, "e")) return;
    }
    if (sectionName === "allowanceItems" && id === "f") {
      if (!validateLTA(processedValue, salaryId, "f")) return;
    }
    if (sectionName === "allowanceItems" && id === "k") {
      if (!validateLTA(processedValue, salaryId, "k")) return;
    }
    if (sectionName === "allowanceItems" && id === "l") {
      if (!validateLTA(processedValue, salaryId, "l")) return;
    }
    if (sectionName === "allowanceItems" && id === "m") {
      if (!validateLTA(processedValue, salaryId, "m")) return;
    }
    // Special handling for "Any Other" item (id === "q")
    if (sectionName === "allowanceItems" && id === "q") {
      // Don't update the value directly for "Any Other" since we use modal
      return;
    }

    setSalaryEntries(prev =>
      prev.map(entry => {
        if (entry.id === salaryId) {
          const updatedSection = entry[sectionName].map(item => {
            if (item.id === id) {
              // For HRA section (id === "k"), we only update the value if it's not coming from child changes
              if (id === "k" && item.child) {
                return item; // Child updates are handled in handleChildInputChange
              }

              const updatedItem = { ...item, value: processedValue };

              if (item.child && item.data && id !== "k") {
                const childTotal = item.data.reduce(
                  (sum, childItem) => sum + Number.parseFloat(childItem.value || "0"),
                  0
                );
                updatedItem.value = childTotal.toFixed(2);
              }

              return updatedItem;
            }
            return item;
          });

          return { ...entry, [sectionName]: updatedSection };
        }
        return entry;
      })
    );
  };

  const handleChildInputChange = (salaryId, sectionName, parentId, childId, value) => {
    // Check if this is an amount field that needs validation
    const amountSections = ['grossSalaryItems', 'allowanceItems', 'deductionItems', 'incomeClaimedForRelief'];
    
    let processedValue = value;
    if (amountSections.includes(sectionName)) {
      // Only allow numbers (0-9) and decimal point
      processedValue = value.replace(/[^0-9.]/g, '');
      // Prevent multiple decimal points
      const parts = processedValue.split('.');
      processedValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : processedValue;
      // Ensure non-negative
      const numericValue = Math.max(0, Number.parseFloat(processedValue || 0));
      processedValue = numericValue.toString();
    }

    // Clear child validation errors when child field becomes valid
    if (processedValue && processedValue.trim()) {
      clearValidationError(`${salaryId}_${sectionName}_${childId}`)
    }

    setSalaryEntries(prev =>
      prev.map(entry => {
        if (entry.id === salaryId) {
          const updatedSection = entry[sectionName].map(item => {
            if (item.id === parentId && item.child) {
              const updatedData = item.data.map(childItem =>
                childItem.id === childId ? { ...childItem, value: processedValue } : childItem
              );

              // Special handling for HRA section
              if (parentId === "k") {
                // Get all necessary values
                const placeOfWork = updatedData.find(d => d.id === "k1")?.value || "";
                const hraReceived = Number(updatedData.find(d => d.id === "k2")?.value || 0);
                const rentPaid = Number(updatedData.find(d => d.id === "k3")?.value || 0);

                // Calculate total basic salary from all entries
                const basicSalary = prev.reduce((sum, e) => {
                  return sum + Number(e.grossSalaryItems[0]?.data?.[0]?.value || 0);
                }, 0);
                const daSalary = prev.reduce((sum, e) => {
                  return sum + Number(e.grossSalaryItems[0]?.data?.[1]?.value || 0);
                }, 0);
                const otherSalary = prev.reduce((sum, e) => {
                  return sum + Number(e.grossSalaryItems[0]?.data?.[2]?.value || 0);
                }, 0);

                const totalBasicSalary = basicSalary + daSalary
                const condition4 = (totalBasicSalary + otherSalary) / 3;
                // Calculate exempt amount (minimum of three conditions)

                const condition1 = hraReceived;
                const condition2 = rentPaid - (0.1 * totalBasicSalary);
                const isMetro = placeOfWork == 1 ? true : false; // Adjust based on your placeOfWork values

                const condition3 = (isMetro ? 0.5 : 0.4) * totalBasicSalary;

                const exemptAmount = Math.max(0, Math.min(condition1, condition2, condition3, condition4));


                // Update exempt amount field
                const updatedWithExempt = updatedData.map(d =>
                  d.id === "k4" ? { ...d, value: exemptAmount.toFixed(2) } : d
                );

                return {
                  ...item,
                  data: updatedWithExempt,
                  value: exemptAmount.toFixed(2) // Set parent value to exempt amount
                };
              }

              // Normal handling for other child items
              const childTotal = updatedData.reduce(
                (sum, childItem) => sum + Number(childItem.value || 0),
                0
              );
              return { ...item, data: updatedData, value: childTotal.toFixed(2) };
            }
            return item;
          });

          return { ...entry, [sectionName]: updatedSection };
        }
        return entry;
      })
    );
  };


  const handleEmployerSave = (employerData) => {
    const employerName = employerData.find((field) => field.id === "a")?.value || ""

    setSalaryEntries((prev) =>
      prev.map((entry) => {
        if (entry.id === activeTab) {
          return {
            ...entry,
            employer: employerData,
            employerName,
            // Preserve existing grossSalaryItems instead of resetting them
            grossSalaryItems: entry.grossSalaryItems || [
              {
                id: "a",
                label: "A. Salary as per section 17(1) / AIS",
                value: "0",
                child: true,
                data: [
                  { id: "a1", label: "i. Basic Salary", value: "0" },
                  { id: "a2", label: "ii. DA (Dearness Allowance)", value: "0" },
                  { id: "a3", label: "iii. Other", value: "0" },
                ],
              },
              { id: "b", label: "B. Value of perquisites as per section 17(2)", value: "0" },
              { id: "c", label: "C. Profit in lieu of salary as per section 17(3)", value: "0" },
              {
                id: "d",
                label: "D. Income from retirement benefit account maintained in a notified country u/s 89A",
                value: "0",
                child: true,
                data: [
                  { id: "d1", label: "i. United States", value: "0" },
                  { id: "d2", label: "ii. United Kingdom", value: "0" },
                  { id: "d3", label: "iii. Canada", value: "0" },
                ],
              },
              {
                id: "e",
                label: "E. Income from retirement benefit account maintained in a country other than notified country u/s 89A",
                value: "0",
              },
            ],
            // Preserve existing allowanceItems instead of resetting them
            allowanceItems: entry.allowanceItems || (entry.id === 1 ? [
              { id: "a", label: "Sec 10(5)-Leave Travel concession/assistance", value: "0", sectionCode: "10(5)" },
              { id: "b", label: "Sec 10(6)-Remuneration received as an official, by whatever name called, of an embassy, high commission etc.", value: "0", sectionCode: "10(6)" },
              { id: "c", label: "Sec 10(7)-Allowances or perquisites paid or allowed as such outside India by the Government to a citizen of India for rendering service outside India", value: "0", sectionCode: "10(7)" },
              { id: "d", label: "Sec 10(10)-Death-cum-retirement gratuity received", value: "0", sectionCode: "10(10)" },
              { id: "e", label: "Sec 10(10A)-Commuted value of pension received", value: "0", sectionCode: "10(10A)" },
              { id: "f", label: "Sec 10(10AA)-Earned leave encashment on Retirement", value: "0", sectionCode: "10(10AA)" },
              { id: "g", label: "Sec 10(10B) First proviso - Compensation limit notified by CG in the Official Gazette", value: "0", sectionCode: "10(10B)(i)" },
              { id: "h", label: "Sec 10(10B) Second proviso - Compensation under scheme approved by the Central Government", value: "0", sectionCode: "10(10B)(ii)" },
              { id: "i", label: "Sec 10(10C)-Amount received/receivable on voluntary retirement or termination of service", value: "0", sectionCode: "10(10C)" },
              { id: "j", label: "Sec 10(10CC)-Tax paid by employer on non-monetary perquisite", value: "0", sectionCode: "10(10CC)" },
              {
                id: "k", label: "Sec 10(13A)-Allowance to meet expenditure incurred on house rent", value: "0", sectionCode: "10(13A)", child: true, data: [
                  { id: "k1", label: "i. Place Of Work", value: "", options: true },
                  { id: "k2", label: "ii. HRA received", value: "" },
                  { id: "k3", label: "iii. Rent Paid", value: "" },
                  { id: "k4", label: "iv. Exempt Allowance", value: "" },
                ],
              },
              { id: "l", label: "Section 10(14)(i) - Allowances referred in sub-clauses (a) to (c) of sub-rule (1) in Rule 2BB", value: "0", sectionCode: "10(14)(i)" },
              { id: "m", label: "Section 10(14)(ii) - Transport allowance granted to certain physically handicapped assessee", value: "0", sectionCode: "10(14)(ii)" },
              { id: "n", label: "10(14)(i)(115BAC)", value: "0", sectionCode: "10(14)(i)(115BAC)" },
              { id: "o", label: "10(14)(ii)(115BAC)", value: "0", sectionCode: "10(14)(ii)(115BAC)" },
              { id: "p", label: "EIC", value: "0", sectionCode: "EIC" },
              { id: "q", label: "Any Other", value: "0", sectionCode: "OTH", is_modal: true },
            ] : []),
            // Preserve existing deductionItems instead of resetting them
            deductionItems: entry.deductionItems || (entry.id === 1 ? [
              { id: "a", label: "Standard Deduction u/s 16(ia)", value: "0" },
              { id: "b", label: "Entertainment Allowance u/s 16(ii)", value: "0" },
              { id: "c", label: "Professional Tax u/s 16(iii)", value: "0" },
            ] : []),
            // Preserve existing incomeClaimedForRelief instead of resetting them
            incomeClaimedForRelief: entry.incomeClaimedForRelief || (entry.id === 1 ? [{ id: "a", label: "Income Claimed for Relief from Taxation u/s 89A", value: "0" }] : []),
            grossExpanded: true,
          }
        }
        return entry
      })
    )

    // Clear employer validation errors when save is successful
    clearValidationError(`${activeTab}_employer_validation`)
    
    toast.success("Employer details updated successfully!")
    setIsEmployerModalOpen(false)
  }

  const constructApiBody = (salaryEntries) => {
    const salaryDeduction = {
      salaryDeductionid: null,
      increliefus89A: 0,
      deductionUs16ia: 0,
      entertainmentAlw16ii: 0,
      professionalTaxUs16iii: 0,
      allwncExemptUs10s: []
    }

    // Process custom other sources from the main state
    const customOtherAllowances = customOtherSources
      .filter(source => source.description && Number.parseFloat(source.amount || "0") > 0)
      .map(source => ({
        allwncExemptUs10Id: source.apiId ? source.apiId : null,
        description: source.description,
        SalOthAmount: Number.parseFloat(source.amount || "0"),
        sectionCode: "OTH",
        scheduleEA1013A: null
      }))

    const employers = salaryEntries.map((entry, index) => {
      if (entry.id === 1) {
        salaryDeduction.increliefus89A = Number.parseFloat(entry.incomeClaimedForRelief[0]?.value || "0")
        salaryDeduction.deductionUs16ia = Number.parseFloat(entry.deductionItems[0]?.value || "0")
        salaryDeduction.entertainmentAlw16ii = Number.parseFloat(entry.deductionItems[1]?.value || "0")
        salaryDeduction.professionalTaxUs16iii = Number.parseFloat(entry.deductionItems[2]?.value || "0")

        // Combine regular allowance items with custom other sources
        const regularAllowances = entry.allowanceItems
          .filter((item) => Number.parseFloat(item.value || "0") > 0 && item.sectionCode !== "OTH")
          .map((item) => ({
            allwncExemptUs10Id: item.apiId ? item.apiId : null,
            description: item.label,
            SalOthAmount: Number.parseFloat(item.value || "0"),
            sectionCode: item.sectionCode,
            scheduleEA1013A: item.id === "k" && item.data ? {
              scheduleEA1013AId: null,
              placeOfWork: item.data[0]?.value || "",
              actlHRARecv: Number.parseFloat(item.data[1]?.value || "0"),
              actlRentPaid: Number.parseFloat(item.data[2]?.value || "0"),
              actlRentPaid10Per: 0,
              sal40Or50Per: 0,
              eligbleExmpAllwncUs13A: Number.parseFloat(item.data[3]?.value || "0"),
            } : null
          }))

        salaryDeduction.allwncExemptUs10s = [...regularAllowances, ...customOtherAllowances]
      }

      const basicSalary = Number.parseFloat(entry.grossSalaryItems[0]?.data?.[0]?.value || "0")
      const daSalary = Number.parseFloat(entry.grossSalaryItems[0]?.data?.[1]?.value || "0")
      const otherSalary = Number.parseFloat(entry.grossSalaryItems[0]?.data?.[2]?.value || "0")

      const usAmount = Number.parseFloat(entry.grossSalaryItems[3]?.data?.[0]?.value || "0")
      const ukAmount = Number.parseFloat(entry.grossSalaryItems[3]?.data?.[1]?.value || "0")
      const canadaAmount = Number.parseFloat(entry.grossSalaryItems[3]?.data?.[2]?.value || "0")

      return {
        employerId: entry.employerId,
        employerName: entry.employer.find(f => f.id === "a")?.value || "",
        tan: entry.employer.find(f => f.id === "b")?.value || "",
        addressLine1: entry.employer.find(f => f.id === "c")?.value || "",
        addressLine2: entry.employer.find(f => f.id === "d")?.value || "",
        city: entry.employer.find(f => f.id === "f")?.value || "",
        country: entry.employer.find(f => f.id === "i")?.value || "",
        pin: entry.employer.find(f => f.id === "h")?.value || "",
        employerCategory: entry.employer.find(f => f.id === "j")?.value || "",
        salary: {
          salaryId: entry.salaryId || null,
          salary17OneBasic: basicSalary,
          salary17OneDA: daSalary,
          salary17OneOth: otherSalary,
          salary17Two: Number.parseFloat(entry.grossSalaryItems[1]?.value || "0"),
          salary17Three: Number.parseFloat(entry.grossSalaryItems[2]?.value || "0"),
          incomeNotified89A: usAmount + ukAmount + canadaAmount,
          grossSalary: basicSalary + daSalary + otherSalary +
            Number.parseFloat(entry.grossSalaryItems[1]?.value || "0") +
            Number.parseFloat(entry.grossSalaryItems[2]?.value || "0") +
            usAmount + ukAmount + canadaAmount +
            Number.parseFloat(entry.grossSalaryItems[4]?.value || "0"),
          incomeNotifiedOther89A: Number.parseFloat(entry.grossSalaryItems[4]?.value || "0"),
          not89ASal: [
            { not89ASalId: null, not89ACountryCode: "US", not89AAmount: usAmount },
            { not89ASalId: null, not89ACountryCode: "UK", not89AAmount: ukAmount },
            { not89ASalId: null, not89ACountryCode: "CA", not89AAmount: canadaAmount },
          ].filter((item) => item.not89AAmount > 0)
        }
      }
    })

    return {
      salaryDeduction,
      employers
    }
  }

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true)

      // Validate custom other sources
      const invalidCustomSources = customOtherSources.filter(
        source => !source.description && Number.parseFloat(source.amount || "0") > 0
      )

      if (invalidCustomSources.length > 0) {
        setValidationErrors(prev => ({
          ...prev,
          'custom_sources_description': "Please provide descriptions for all custom income sources with amounts greater than 0"
        }))
        setIsSubmitting(false)
        return
      }

      if (!validateEntertainmentAllowance(salaryEntries)) {
        setValidationErrors(prev => ({
          ...prev,
          'entertainment_allowance_validation': "Entertainment allowance validation failed. It should be ≤ ₹5000 or 20% of total basic salary, and at least one employer must be Government/PSU."
        }))
        setIsSubmitting(false)
        return
      }

      if (!validateEmployerCategory(salaryEntries)) {
        setValidationErrors(prev => ({
          ...prev,
          'employer_category_validation': "Cannot select 'Not Applicable' when gross salary is entered"
        }))
        setIsSubmitting(false)
        return
      }

      if (!validateExemptAllowances(salaryEntries)) {
        setValidationErrors(prev => ({
          ...prev,
          'exempt_allowances_validation': "Exempt allowances cannot exceed total gross salary"
        }))
        setIsSubmitting(false)
        return
      }

      const body = constructApiBody(salaryEntries)

      console.log("Submitting body:", body)

      const isValidData = !body.employers?.some(
        (employer) => employer?.salary?.deductionUs16?.professionalTaxUs16iii > 5000
      )

      if (!isValidData) {
        toast.info("Professional tax cannot exceed ₹5000.")
        setIsSubmitting(false)
        return
      }

      // Guard: ensure deductions do not exceed prospective gross after this save
      const guard = await validateDeductionsVsGrossForProspectiveUpdate({
        incomeFromSalary: overallTotals.totalGrossSalary || 0,
      })
      if (!guard.ok) {
        toast.error(exceedDeductionErrorMessage)
        setIsSubmitting(false)
        return
      }

      const response = await axios.patch(
        `${config.apiUrl}/income/salary/${localStorage.getItem("PAN_NO")}/${localStorage.getItem("YEAR")}`,
        body,
      )
      localStorage.setItem('total_salary', JSON.stringify(overallTotals))

      if (response.data && response.data.employers) {
        setSalaryEntries((prev) =>
          prev.map((entry, index) => ({
            ...entry,
            employerId: response.data.employers[index]?.employerId || entry.employerId,
            salaryId: response.data.employers[index]?.salary?.salaryId || entry.salaryId,
          })),
        )
      }

      await taxComputation(overallTotals.totalGrossSalary, overallTotals.totalNetSalary)

      // Clear all validation errors on successful submission
              setValidationErrors({})
        setIsSubmitting(false)
        const saved = markPageAsSaved('salary')
        if (saved) {
          toast.success("All salary entries updated successfully!")
        } else {
          toast.error("Please save your profile first before saving salary details!")
        }
    } catch (error) {
      setIsSubmitting(false)
      setValidationErrors(prev => ({
        ...prev,
        'submission_error': error.response?.data?.message || "Something went wrong!"
      }))
      console.error("Error:", error)
    }
  }

  const taxComputation = async (grossSalary, netSalary) => {
    try {
      const pan = localStorage.getItem("PAN_NO")
      const year = localStorage.getItem("YEAR")

      const [oldRes, newRes] = await Promise.all([
        axios.get(`${config.apiUrl}/user/tax-computation/${pan}/${year}/OLD`),
        axios.get(`${config.apiUrl}/user/tax-computation/${pan}/${year}/NEW`)
      ])

      const prepareRegimeData = (data, regime, salary) => {
        return {
          ...data,
          taxComputationId: data?.taxComputationId || null,
          regime,
          incomeFromSalary: salary || 0,
        }
      }

      // Calculate new regime taxable salary
      const firstEntry = salaryEntries.find(entry => entry.id === 1);
      let newRegimeTaxableSalary = grossSalary;
      
      if (firstEntry) {
        const newRegimeExemptAllowances = calculateNewRegimeExemptAllowances(firstEntry);
        const totalRelief = firstEntry.incomeClaimedForRelief.reduce(
          (sum, item) => sum + Number.parseFloat(item.value || "0"), 0
        );
        const newRegimeBaseAmount = grossSalary - newRegimeExemptAllowances - totalRelief;
        const newRegimeStandardDeduction = Math.min(newRegimeBaseAmount, 75000);
        newRegimeTaxableSalary = Math.max(0, newRegimeBaseAmount - newRegimeStandardDeduction);
      }

      const oldPayload = {
        taxComputation: prepareRegimeData(
          oldRes.data?.taxComputation,
          "OLD",
          netSalary
        )
      }

      const newPayload = {
        taxComputation: prepareRegimeData(
          newRes.data?.taxComputation,
          "NEW",
          newRegimeTaxableSalary
        )
      }

      await Promise.all([
        axios.patch(`${config.apiUrl}/user/tax-computation/${pan}/${year}/OLD`, oldPayload),
        axios.patch(`${config.apiUrl}/user/tax-computation/${pan}/${year}/NEW`, newPayload)
      ])

    } catch (error) {
      console.error("Error in taxComputation:", error)
    }
  }

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const response = await axios.get(
          `${config.apiUrl}/income/salary/${localStorage.getItem("PAN_NO")}/${localStorage.getItem("YEAR")}`,
        )
        const data = response.data
        const salaryDeduction = data.salaryDeduction || {}
        // Extract custom other sources (sectionCode "OTH") from API
        const customSources = salaryDeduction.allwncExemptUs10s
          ?.filter(item => item.sectionCode === "OTH")
          ?.map((item, index) => ({
            id: index + 1,
            apiId: item.allwncExemptUs10Id || null,
            description: item.description,
            amount: (item.SalOthAmount || 0).toFixed(2)
          })) || []

        setCustomOtherSources(customSources)
        if (data.employers && data.employers.length > 0) {
          const mappedEntries = data.employers.map((employer, index) => {
            const salary = employer.salary || {}

            return {
              ...createDefaultSalaryData(index + 1),
              employerId: employer.employerId,
              salaryId: salary.salaryId,
              employerName: employer.employerName || "",
              employerCategory: employer.employerCategory || "",
              employer: [
                { id: "a", label: "Name", value: employer.employerName || "" },
                { id: "b", label: "TAN", value: employer.tan || "" },
                { id: "c", label: "Flat / Building", value: employer.addressLine1 || "" },
                { id: "d", label: "Street / Road", value: employer.addressLine2 || "" },
                { id: "e", label: "Area / Locality", value: "" },
                { id: "f", label: "Town / City", value: employer.city || "" },
                { id: "g", label: "State", value: "" },
                { id: "h", label: "Pin Code", value: employer.pin || "" },
                { id: "i", label: "Country", value: employer.country || "" },
                { id: "j", label: "Employer Category", value: employer.employerCategory || "", options: [] },
              ],
              grossSalaryItems: [
                {
                  id: "a",
                  label: "A. Salary as per section 17(1) / AIS",
                  value: (
                    (salary.salary17OneBasic || 0) +
                    (salary.salary17OneDA || 0) +
                    (salary.salary17OneOth || 0)
                  ).toFixed(2),
                  child: true,
                  data: [
                    { id: "a1", label: "i. Basic Salary", value: (salary.salary17OneBasic || 0).toFixed(2) },
                    { id: "a2", label: "ii. DA (Dearness Allowance)", value: (salary.salary17OneDA || 0).toFixed(2) },
                    { id: "a3", label: "iii. Other", value: (salary.salary17OneOth || 0).toFixed(2) },
                  ],
                },
                {
                  id: "b",
                  label: "B. Value of perquisites as per section 17(2)",
                  value: (salary.salary17Two || 0).toFixed(2),
                },
                {
                  id: "c",
                  label: "C. Profit in lieu of salary as per section 17(3)",
                  value: (salary.salary17Three || 0).toFixed(2),
                },
                {
                  id: "d",
                  label: "D. Income from retirement benefit account maintained in a notified country u/s 89A",
                  value: (salary.incomeNotified89A || 0).toFixed(2),
                  child: true,
                  data: [
                    {
                      id: "d1",
                      label: "i. United States",
                      value: (salary.not89ASal?.find((n) => n.not89ACountryCode === "US")?.not89AAmount || 0).toFixed(2),
                    },
                    {
                      id: "d2",
                      label: "ii. United Kingdom",
                      value: (salary.not89ASal?.find((n) => n.not89ACountryCode === "UK")?.not89AAmount || 0).toFixed(2),
                    },
                    {
                      id: "d3",
                      label: "iii. Canada",
                      value: (salary.not89ASal?.find((n) => n.not89ACountryCode === "CA")?.not89AAmount || 0).toFixed(2),
                    },
                  ],
                },
                {
                  id: "e",
                  label: "E. Income from retirement benefit account maintained in a country other than notified country u/s 89A",
                  value: (salary.incomeFromSal || 0).toFixed(2),
                },
              ],
              allowanceItems: index === 0 ? createDefaultSalaryData(1).allowanceItems.map((defaultItem, idx) => {
                // Filter out OTH items since we handle them separately in customOtherSources
                const apiItem = salaryDeduction.allwncExemptUs10s?.find((a) =>
                  a.sectionCode === defaultItem.sectionCode && a.sectionCode !== "OTH"
                )
                return {
                  ...defaultItem,
                  value: (apiItem?.SalOthAmount || 0).toFixed(2),
                  apiId: (apiItem?.allwncExemptUs10Id || null),
                  data: defaultItem.child && apiItem?.scheduleEA1013A
                    ? [
                      { id: "k1", label: "i. Place Of Work", value: apiItem.scheduleEA1013A.placeOfWork || "", options: true },
                      {
                        id: "k2",
                        label: "ii. HRA received",
                        value: (apiItem.scheduleEA1013A.actlHRARecv || 0).toFixed(2),
                      },
                      {
                        id: "k3",
                        label: "iii. Rent Paid",
                        value: (apiItem.scheduleEA1013A.actlRentPaid || 0).toFixed(2),
                      },
                      {
                        id: "k4",
                        label: "iv. Exempt Allowance",
                        value: (apiItem.scheduleEA1013A.eligbleExmpAllwncUs13A || 0).toFixed(2),
                      },
                    ]
                    : defaultItem.data,
                }
              }) : [],
              deductionItems: index === 0 ? [
                {
                  id: "a",
                  label: "Standard Deduction u/s 16(ia)",
                  value: (data.salaryDeduction?.deductionUs16ia || 0).toFixed(2),
                },
                {
                  id: "b",
                  label: "Entertainment Allowance u/s 16(ii)",
                  value: (data.salaryDeduction?.entertainmentAlw16ii || 0).toFixed(2),
                },
                {
                  id: "c",
                  label: "Professional Tax u/s 16(iii)",
                  value: (data.salaryDeduction?.professionalTaxUs16iii || 0).toFixed(2),
                },
              ] : [],
              incomeClaimedForRelief: index === 0 ? [
                {
                  id: "a",
                  label: "Income Claimed for Relief from Taxation u/s 89A",
                  value: (salaryDeduction.increliefus89A || 0).toFixed(2),
                },
              ] : [],
            }
          })

          setSalaryEntries(mappedEntries)
          setActiveTab(1)
          // Clear fetch error if successful
          clearValidationError(`fetch_salary_data_error`)
        }
      } catch (error) {
        console.log("Error fetching data:", error)
        setValidationErrors(prev => ({
          ...prev,
          [`fetch_salary_data_error`]: "Failed to fetch salary data."
        }))
      }
    }

    fetchProfileData()
  }, [])

  const activeSalary = salaryEntries.find((entry) => entry.id === activeTab)
  const activeCalculations = getCalculationsForEntry(activeTab)
  const allowedCategories = new Set(["CGOV", "SGOV", "PSU"])
  const hasValidEmployer = salaryEntries.some(entry => {
    const employerCategory = entry.employer?.find(field => field.id === "j")?.value
    return employerCategory && allowedCategories.has(employerCategory)
  })

  // Prepare salary data for tax comparison sidebar
  const salaryComparisonData = useMemo(() => {
    const firstEntry = salaryEntries.find(entry => entry.id === 1);
    if (!firstEntry) return null;

    const calculations = getCalculationsForEntry(firstEntry.id);
    const relief = firstEntry.incomeClaimedForRelief.reduce((sum, item) => 
      sum + Number.parseFloat(item.value || "0"), 0);
    const standardDeduction = Number.parseFloat(firstEntry.deductionItems.find(item => item.id === "a")?.value || "0");

    // Old regime calculation
    const oldRegimeTaxable = Math.max(0, overallTotals.totalGrossSalary - calculations.totalAllowanceItems - relief - calculations.totalDeduction);
    
    // New regime calculation: Gross Salary - Exempt Allowances - Relief - Standard Deduction
    // Standard deduction is minimum of (Gross - Exempt Allowances - Relief) or 75000
    const newRegimeExemptAllowances = calculateNewRegimeExemptAllowances(firstEntry);
    const newRegimeBaseAmount = overallTotals.totalGrossSalary - newRegimeExemptAllowances - relief;
    const newRegimeStandardDeduction = Math.min(newRegimeBaseAmount, 75000);
    const newRegimeTaxable = Math.max(0, newRegimeBaseAmount - newRegimeStandardDeduction);
    
    const difference = oldRegimeTaxable - newRegimeTaxable;

    return {
      formatCurrency,
      totalGrossSalary: overallTotals.totalGrossSalary,
      totalAllowanceItems: calculations.totalAllowanceItems,
      newRegimeExemptAllowances,
      totalRelief: relief,
      totalDeduction: calculations.totalDeduction,
      standardDeduction,
      newRegimeStandardDeduction,
      oldRegimeTaxable,
      newRegimeTaxable,
      difference
    };
  }, [salaryEntries, overallTotals, formatCurrency, getCalculationsForEntry]);

  if (!activeSalary) return null

  return (
    <Dashboard 
      salaryData={salaryComparisonData} 
      showTaxComparison={true}
    >
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <div className="p-6 pt-0 pl-0">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
          <h2 className="text-2xl font-semibold text-gray-800"></h2>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <button
              type="button"
              onClick={addSalaryEntry}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors"
            >
              <Plus size={20} />
              Add New Salary
            </button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSubmit}
              disabled={isSubmitting}
              className={`flex items-center justify-center gap-2 px-6 py-2 text-white transition-colors ${isSubmitting
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700"
                }`}
            >
              {isSubmitting ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Number.POSITIVE_INFINITY, ease: "linear" }}
                  className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                />
              ) : (
                <Save className="w-5 h-5" />
              )}
              {isSubmitting ? "Saving..." : "Update All Salaries"}
            </motion.button>
          </div>
        </div>

        {/* Salary Management Box with Tabs */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm mb-6">

          {/* Salary Tabs */}
          <div className="border-b border-gray-200">
            <div className="flex flex-wrap gap-2 p-4">
            {salaryEntries.map((entry, index) => (
              <div key={entry.id} className="relative">
                <button
                  type="button"
                  onClick={() => setActiveTab(entry.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                      activeTab === entry.id
                        ? "bg-cyan-500 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                >
                  <Briefcase size={16} />
                  <span className="hidden sm:inline">Salary {index + 1}</span>
                  <span className="sm:hidden">S{index + 1}</span>
                  {entry.employerName && (
                    <span className="text-xs opacity-75 hidden md:inline">
                      ({entry.employerName.substring(0, 10)}
                      {entry.employerName.length > 10 ? "..." : ""})
                    </span>
                  )}
                  {salaryEntries.length > 1 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        removeSalaryEntry(entry.id)
                      }}
                      className="ml-2 p-1 rounded-full hover:bg-red-500 hover:text-white transition-colors"
                    >
                      <X size={14} />
                    </button>
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>

          {/* Active Salary Content */}
          <div className="p-4">
        <motion.div
          key={activeSalary.id}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
          className="space-y-4"
        >
          <EmployerModal
            employerName={activeSalary.employerName}
            onClick={() => setIsEmployerModalOpen(true)}
          />

          {/* Employer Details Section */}
          <EmployerSection
            employerName={activeSalary.employerName}
            employerCategory={activeSalary.employer.find(f => f.id === "j")?.value || ""}
            onClick={() => setIsEmployerModalOpen(true)}
          />

                     <SalarySection
             title="Gross Salary"
             amount={formatCurrency(activeCalculations.totalGrossSalary)}
             isExpanded={activeSalary.grossExpanded}
             onToggle={() => updateSalaryEntry(activeSalary.id, "grossExpanded", !activeSalary.grossExpanded)}
             items={activeSalary.grossSalaryItems}
             isMenu={true}
             onInputChange={(id, value) => handleInputChange(activeSalary.id, "grossSalaryItems", id, value)}
             onChildInputChange={(parentId, childId, value) =>
               handleChildInputChange(activeSalary.id, "grossSalaryItems", parentId, childId, value)
             }
             salaryEntries={salaryEntries}
             setValidationErrors={setValidationErrors}
             clearValidationError={clearValidationError}
           />
            </motion.div>
          </div>
        </div>

        {/* Common Deductions & Allowances - Tabbed interface for each item */}
        {salaryEntries.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm mb-6">
            {(() => {
              const firstEntry = salaryEntries.find(entry => entry.id === 1);
              const firstCalculations = firstEntry ? getCalculationsForEntry(firstEntry.id) : null;
              
              if (!firstEntry || !firstCalculations) return null;
              
              const commonTabs = [
                {
                  id: "allowances",
                  label: "Exempt Allowances",
                  amount: formatCurrency(firstCalculations.totalAllowanceItems)
                },
                {
                  id: "relief",
                  label: "Relief",
                  amount: formatCurrency(firstEntry.incomeClaimedForRelief[0]?.value || "0")
                },
                {
                  id: "deductions",
                  label: "Deduction u/s 16",
                  amount: formatCurrency(firstCalculations.totalDeduction)
                }
              ];
              
              return (
                <>
                  {/* Common Deduction Tabs */}
                  <div className="border-b border-gray-200">
                    <div className="flex flex-wrap gap-2 p-4">
                      {commonTabs.map((tab) => (
                        <button
                          key={tab.id}
                          type="button"
                          onClick={() => setCommonDeductionTab(tab.id)}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                            commonDeductionTab === tab.id
                              ? "bg-cyan-500 text-white"
                              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                          }`}
                        >
                          <span className="text-sm font-medium">{tab.label}</span>
                          <span className="text-xs opacity-75">({tab.amount})</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Common Deduction Content */}
                  <div className="p-4">
                    <motion.div
                      key={commonDeductionTab}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      {commonDeductionTab === "allowances" && (
                            <SalarySection
                title="Less: Exempt Allowances"
                amount={formatCurrency(firstCalculations.totalAllowanceItems)}
                isExpanded={firstEntry.allowancesExpanded}
                onToggle={() => updateSalaryEntry(firstEntry.id, "allowancesExpanded", !firstEntry.allowancesExpanded)}
                items={firstEntry.allowanceItems}
                isMenu={true}
                onInputChange={(id, value) => handleInputChange(firstEntry.id, "allowanceItems", id, value)}
                totalBasicSalary={salaryCalculations.reduce((sum, calc) => sum + calc.totalBasicSalary, 0)}
                totalGrossSalary={overallTotals.totalGrossSalary}
                employmentCategory={firstEntry.employer.find(f => f.id === "j")?.value || ""}
                totalAllowanceAmount={firstCalculations.totalAllowanceItems}
                salaryEntries={salaryEntries}
                onChildInputChange={(parentId, childId, value) =>
                  handleChildInputChange(firstEntry.id, "allowanceItems", parentId, childId, value)
                }
                customOtherSources={customOtherSources}
                setCustomOtherSources={setCustomOtherSources}
                showOtherIncomeModal={showOtherIncomeModal}
                setShowOtherIncomeModal={setShowOtherIncomeModal}
                setValidationErrors={setValidationErrors}
                clearValidationError={clearValidationError}
              />
                      )}

                                            {commonDeductionTab === "relief" && (
              <SalarySection
                title="Less: Relief"
                amount={formatCurrency(firstEntry.incomeClaimedForRelief[0]?.value || "0")}
                isExpanded={firstEntry.lessIncomeExpanded}
                onToggle={() => updateSalaryEntry(firstEntry.id, "lessIncomeExpanded", !firstEntry.lessIncomeExpanded)}
                items={firstEntry.incomeClaimedForRelief}
                isMenu={true}
                onInputChange={(id, value) => handleInputChange(firstEntry.id, "incomeClaimedForRelief", id, value)}
                salaryEntries={salaryEntries}
                setValidationErrors={setValidationErrors}
                clearValidationError={clearValidationError}
              />
                      )}

                                            {commonDeductionTab === "deductions" && (
              <SalarySection
                title="Deduction u/s 16"
                amount={formatCurrency(firstCalculations.totalDeduction)}
                isExpanded={firstEntry.deductionItemsExpanded}
                onToggle={() => updateSalaryEntry(firstEntry.id, "deductionItemsExpanded", !firstEntry.deductionItemsExpanded)}
                items={firstEntry.deductionItems}
                isMenu={true}
                onInputChange={(id, value) => handleInputChange(firstEntry.id, "deductionItems", id, value)}
                employmentCategory={hasValidEmployer}
                totalBasicSalary={salaryEntries.reduce((sum, entry) => {
                  const basicSalary = Number.parseFloat(
                    entry.grossSalaryItems[0]?.data?.find(item => item.id === "a1")?.value || "0"
                  )
                  return sum + basicSalary
                          }, 0)}
                setValidationErrors={setValidationErrors}
                clearValidationError={clearValidationError}
                        />
                      )}


        </motion.div>
                  </div>
                </>
              );
            })()}
          </div>
        )}



        <div className="mt-6 bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-gray-800 mb-2">Summary</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-sm text-gray-600">Total Entries</div>
              <div className="text-xl font-semibold text-cyan-600">{salaryEntries.length}</div>
            </div>
            <div className="text-center">
              <div className="text-sm text-gray-600">Total Gross Salary</div>
              <div className="text-xl font-semibold text-green-600">
                {formatCurrency(overallTotals.totalGrossSalary)}
              </div>
            </div>
            <div className="text-center">
              <div className="text-sm text-gray-600">Total Net Salary</div>
              <div className="text-xl font-semibold text-blue-600">{formatCurrency(overallTotals.totalNetSalary)}</div>
            </div>
          </div>
        </div>
      </div>

      <EmployerModal
        isOpen={isEmployerModalOpen}
        onClose={() => setIsEmployerModalOpen(false)}
        employerData={salaryEntries.find(entry => entry.id === activeTab)?.employer || activeSalary.employer}
        onSave={handleEmployerSave}
        employerName={salaryEntries.find(entry => entry.id === activeTab)?.employerName || activeSalary.employerName}
        salaryId={activeTab}
        onValidationChange={(errs) => {
          // Merge employer modal errors into a transient store on the active entry
          setSalaryEntries(prev => prev.map(e => e.id === activeTab ? { ...e, __employerModalErrors: errs } : e))
        }}
      />

      <ValidationErrorSidebar
        errors={(() => {
          const entry = salaryEntries.find(e => e.id === activeTab)
          const errs = {}
          if (!entry) return errs

          // Employer TAN and PIN validations (from inline state)
          ;(entry.employer || []).forEach((field) => {
            if (field.id === 'b' && field.value) {
              const tan = (field.value || '').trim().toUpperCase()
              if (tan && !EMPLOYER_TAN_REGEX.test(tan)) {
                errs[`${entry.id}_employer_b`] = 'Invalid TAN format.'
              }
            }
            if (field.id === 'h' && field.value) {
              const pin = (field.value || '').trim()
              if (pin.length === 6 && !centralizedPatterns.PIN.test(pin)) {
                errs[`${entry.id}_employer_h`] = 'Invalid PIN format.'
              }
            }
            if (field.id === 'a') {
              const cat = (entry.employer.find(f => f.id === 'j')?.value || '').trim()
              const nameVal = (field.value || '').trim()
              if (cat !== 'NA' && !nameVal) {
                errs[`${entry.id}_employer_a`] = 'Employer name is required'
              }
            }
          })

          // Include live errors coming from EmployerModal typing
          if (entry.__employerModalErrors) {
            Object.assign(errs, entry.__employerModalErrors)
          }

          // Include centralized validation errors
          Object.assign(errs, validationErrors)

          // Child form validations for active entry: require child values when parent value > 0
          const sections = ['grossSalaryItems', 'allowanceItems', 'deductionItems']
          sections.forEach((sectionName) => {
            const items = entry[sectionName] || []
            items.forEach((item) => {
              if (item && item.child && Array.isArray(item.data)) {
                const parentVal = Number.parseFloat(item.value || '0')
                if (parentVal > 0) {
                  item.data.forEach((child) => {
                    const val = (child?.value ?? '').toString().trim()
                    if (!val) {
                      errs[`${entry.id}_${sectionName}_${child.id}`] = `${child.label} is required`
                    }
                  })
                }
              }
            })
          })

          return errs
        })()}
        isVisible={true}
        onFieldClick={(fieldKey) => {
          const [salaryId, sectionName, fieldId] = fieldKey.split('_')
          const inputElement = document.querySelector(`input[data-salary-id="${salaryId}"][data-section="${sectionName}"][data-field="${fieldId}"]`)
          if (inputElement) {
            inputElement.focus()
            inputElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        }}
        title="Salary Validation Errors"
      />

      {showOtherIncomeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-4xl space-y-4 shadow-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-800">Any Other Income Sources</h2>
              <button
                onClick={() => setShowOtherIncomeModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              {customOtherSources.map((source, index) => (
                <div key={source.id} className="border rounded-lg p-4 space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="font-medium text-gray-700">Source {index + 1}</h3>
                    {customOtherSources.length > 1 && (
                      <button
                        onClick={() => handleRemoveCustomOtherSource(source.id,source.apiId)}
                        className="p-2 text-red-500 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Description</label>
                      <input
                        type="text"
                        className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        placeholder="Enter description"
                        value={source.description}
                        onChange={(e) => updateCustomOtherSource(source.id, "description", e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Amount</label>
                      <input
                        type="number"
                        className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        placeholder="Enter amount"
                        value={source.amount}
                        onChange={(e) => {
                          // Only allow numbers (0-9) and decimal point
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          // Prevent multiple decimal points
                          const parts = value.split('.');
                          const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                          const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0)); // Ensure non-negative
                          updateCustomOtherSource(source.id, "amount", numericValue.toString());
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-between items-center pt-4">
              <button
                onClick={addCustomOtherSource}
                className="flex items-center gap-2 px-4 py-2 text-cyan-600 border border-cyan-600 rounded-lg hover:bg-cyan-50"
              >
                <Plus className="w-4 h-4" />
                Add Another Source
              </button>
              <div className="flex gap-3">
                <button
                  className="px-4 py-2 bg-gray-300 rounded-md hover:bg-gray-400"
                  onClick={() => {
                    // Clear custom income source validation errors when modal is closed
                    customOtherSources.forEach(source => {
                      clearValidationError(`custom_income_description_${source.id}`)
                      clearValidationError(`custom_income_amount_${source.id}`)
                    })
                    setShowOtherIncomeModal(false)
                  }}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 bg-cyan-600 text-white rounded-md hover:bg-cyan-700"
                  onClick={() => {
                    // Clear custom income source validation errors when modal is saved
                    customOtherSources.forEach(source => {
                      clearValidationError(`custom_income_description_${source.id}`)
                      clearValidationError(`custom_income_amount_${source.id}`)
                    })
                    setShowOtherIncomeModal(false)
                  }}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Dashboard>
  )
}